<template>
  <v-container fluid>
    <v-speed-dial
      v-model="fab"
      bottom
      right
      fixed
      open-on-hover
      :direction="'top'"
      :transition="'slide-y-reverse-transition'"
      v-if="!['operations'].includes($store.state.auth.profile.role)"
    >
      <template v-slot:activator>
        <v-btn v-model="fab" color="primary" dark fab>
          <v-icon v-if="fab">
            mdi-close
          </v-icon>
          <v-icon v-else>
            mdi-plus
          </v-icon>
        </v-btn>
      </template>
      <v-tooltip left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            fab
            dark
            color="green"
            v-on="on"
            @click="dialogForm = {action: 'Create Vessel', showForm: true, component: 'VesselApiForm', vessel: {}}"
          >
            <v-icon>mdi-web</v-icon>
          </v-btn>
        </template>
        <span>Create via API</span>
      </v-tooltip>
      <v-tooltip left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            fab
            dark
            color="indigo"
            v-on="on"
            @click="dialogForm = {action: 'Create Vessel', showForm: true, component: 'VesselManualForm', vessel: {}}"
          >
            <v-icon>mdi-incognito</v-icon>
          </v-btn>
        </template>
        <span>Create Manually</span>
      </v-tooltip>
    </v-speed-dial>
    <v-card>
      <v-card-title>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search (Name, IMO Number, Principal, Billing Party)"
          single-line
          hide-details
          :no-data-text="'No data found'"
          :no-results-text="'No results found'"
          clearable
        ></v-text-field>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :sort-by="['id']"
        :sort-desc="[true]"
        :server-items-length="pagination.total_count"
        :items-per-page="pagination.per_page"
        :page="pagination.current_page"
        @update:page="updatePage"
        hide-default-footer
      >
        <template v-slot:progress>
          <div class="full-width text-center ma-4">
            <v-progress-circular
              indeterminate
              color="primary"
              :size="60"
              :width="5"
            ></v-progress-circular>
          </div>
        </template>
        <template v-if="!['operations'].includes($store.state.auth.profile.role)" v-slot:item.edit="{ item }">
          <v-btn icon @click="dialogForm = {action: 'Edit Vessel', showForm: true, component: 'VesselManualForm', vessel: Object.assign({}, item)}">
            <v-icon>mdi-account-edit</v-icon>
          </v-btn>
        </template>
        <template v-slot:item.vessel_owner="{ item }">
          {{ item.vessel_owner_string }}
        </template>
      </v-data-table>
      <v-pagination
        v-model="pagination.current_page"
        :length="pagination.total_pages"
        :total-visible="7"
        @input="updatePage"
        class="mt-4"
      ></v-pagination>
    </v-card>
    <component
      :is="dialogForm.component" 
      :action="dialogForm.action"
      :showForm="dialogForm.showForm"
      :owners="vesselOwners"
      :principal="principal"
      @formSubmitted="resetDialog();getVessels();"
      @closeDialog="resetDialog()"
      :vessel="dialogForm.vessel" />
  </v-container>
</template>

<script>
  import VesselApiForm from './blocks/VesselApiForm';
  import VesselManualForm from './blocks/VesselManualForm';

  export default {
    name: 's-vessel',
    components: {
      VesselApiForm,
      VesselManualForm
    },
    data() {
      return {
        dialogForm: {
          component: '',
          showForm: false,
          action: '',
        },
        fab: false,
        search: '',
        loading: false,
        headers: [],
        items: [],
        vesselOwners: [],
        principal: [],
        pagination: {
          current_page: 1,
          per_page: 25,
          total_count: 0,
          total_pages: 1
        },
        searchTimeout: null
      };
    },
    methods: {
      resetDialog() {
        this.dialogForm = {showForm: false, action: '', component: ''};
      },
      setHeaders() {
        const billingPartyAccess = !['operations'].includes(this.$store.state.auth.profile.role);
        const commonHeaders = [
          {text: 'Id', value: 'id', align: 'center'},
          {text: 'Name', value: 'name', align: 'center'},
          {text: 'Vessel Type', value: 'vessel_type', align: 'center'},
          {text: 'Call Sign', value: 'call_sign', align: 'center'},
          {text: 'IMO Number', value: 'imo_number', align: 'center'},
          {text: 'Flag', value: 'flag', align: 'center'},
          {text: 'Principal', value: 'principal_name', align: 'center'},
          {text: 'Registered Ownership', value: 'registered_ownership', align: 'center'},
          {text: 'Gross Tonnage', value: 'gross_tonnage', align: 'center'}
        ];
        if (billingPartyAccess) {
          commonHeaders.push({ text: 'Billing Party', value: 'vessel_owner_string', align: 'center' });
          commonHeaders.push({ text: 'Edit', value: 'edit', align: 'center' });
        }
        this.headers = commonHeaders;
      },
      updatePage(page) {
        this.pagination.current_page = page;
        this.getVessels();
      },
      onSearchInput() {
        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout);
        }
        this.searchTimeout = setTimeout(() => {
          this.pagination.current_page = 1; // Reset to first page on new search
          this.getVessels();
        }, 500);
      },
      getVessels() {
        this.loading = true;
        const params = {
          page: this.pagination.current_page,
          per_page: this.pagination.per_page
        };
        
        if (this.search && this.search.trim()) {
          params.search = this.search.trim();
        }
        
        this.$api.vessels.list(params)
          .then((response) => {
            this.items = response.vessels;
            this.vesselOwners = response.vessel_owners;
            this.principal = response.principal;
            this.pagination = response.pagination;
            this.loading = false;
          }).catch((err) => {
            this.loading = false;
            this.$store.dispatch('setAlert', {
              status: 'error',
              show: true,
              message: err.message,
            });
          });
      },
    },
    watch: {
      search() {
        this.onSearchInput();
      }
    },
    created() {
      this.getVessels();
      this.setHeaders();
    },
  };
</script>